import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser, logout as apiLogout } from "../api/auth";
import { LOGIN_REDIRECT_URL } from "../api/configApi";

export const AuthContext = createContext();

const FULL_ACCESS = "*";

// Fallback pour les comptes STORE "legacy" (propriétaires) dont
// permissions est vide/absent en base — même pattern que
// teqa_confirmation/src/context/AuthContext.jsx::LEGACY_ROLE_PERMISSION_MAP.
// Sans ce fallback, un propriétaire sans permissions explicites voit tout
// son menu masqué (canSeeItem([], code) === false pour chaque item).
const LEGACY_ROLE_PERMISSION_MAP = {
  STORE: [
    "dashboard.view",
    "orders.view",
    "orders.assign",
    "orders.view_stats",
    "collaborations.view",
    "collaborations.create",
    "wallets.view_own",
    "commissions.view_own",
    "invoices.view_own",
    "messaging.view_own",
    "team.view",
    "team.create",
    "team.update",
    "team.manage_permissions",
  ],
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizePermissions = useCallback((currentUser) => {
    if (!currentUser) return [];

    const permissions = currentUser.permissions || [];
    // Un compte enfant garde ses permissions telles quelles (même vides —
    // ça veut dire "aucun accès accordé", pas "accès legacy complet").
    if (permissions.length > 0 || currentUser.is_child) {
      return permissions;
    }

    return LEGACY_ROLE_PERMISSION_MAP[currentUser.role] || [];
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Ignore l'échec de l'appel logout, on nettoie quand même la session locale.
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      window.location.href = LOGIN_REDIRECT_URL;
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const res = await getCurrentUser();
      const userData = res.data;

      if (userData.role !== "STORE") {
        await logout();
        return;
      }

      setUser({
        ...userData,
        permissions: normalizePermissions(userData),
      });
    } catch (err) {
      console.log("ERROR:", err.response?.status);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [logout, normalizePermissions]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      const permissions = user.permissions || [];
      return permissions.includes(FULL_ACCESS) || permissions.includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissionList) => {
      if (!user) return false;
      return permissionList.some((permission) => hasPermission(permission));
    },
    [hasPermission, user]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      logout,
      refreshUser: loadUser,
      hasPermission,
      hasAnyPermission,
      isParent: !!user?.is_parent,
      isChild: !!user?.is_child,
    }),
    [user, loading, logout, loadUser, hasPermission, hasAnyPermission]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
